import { useEffect, useMemo, useState } from "react";
import { useField } from "react-final-form";
import { Check, Error as ErrorIcon } from "@mui/icons-material";
import { Box, CircularProgress, Tooltip, Typography } from "@mui/material";
import { useDebouncedCallback } from "use-debounce";

import analytics from "fond/analytics";
import { TextField } from "fond/form/fields";
import { required } from "fond/utils/validation";

import { UpdateVersionReturn } from "./types";

type ProjectCodeFieldProps = {
  projectCode?: string | null;
  handleUpdateProjectCode: (projectCode: string) => Promise<UpdateVersionReturn>;
};

const DEFAULT_PROJECT_CODE = "N2P";

export const ProjectCodeField: React.FC<ProjectCodeFieldProps> = ({ projectCode, handleUpdateProjectCode }: ProjectCodeFieldProps) => {
  const [projectCodeState, setProjectCodeState] = useState<"saving" | "success" | "error" | null>(null);
  const {
    input: { value: projectCodeValue },
  } = useField("ProjectCode");

  const handleProjectCodeChange = useDebouncedCallback(async (value: string) => {
    const val = value.trim();
    // an empty of unchanged code should not trigger an update
    if (!val || val === projectCode) {
      setProjectCodeState(null);
      return;
    }

    setProjectCodeState("saving");

    if (val !== projectCode) {
      const res = await handleUpdateProjectCode(val);
      if ("error" in res) {
        setProjectCodeState("error");
        return;
      }
    }

    setProjectCodeState("success");
    analytics.track("design.nbn.project-code.saved");
  }, 500);

  useEffect(() => {
    if (projectCodeValue && projectCodeValue !== projectCode) {
      setProjectCodeState("saving");
    }
    handleProjectCodeChange(projectCodeValue);
  }, [handleProjectCodeChange, projectCode, projectCodeValue]);

  const projectCodeEndAdornment = useMemo(() => {
    if (projectCodeState === "saving") {
      return <CircularProgress size={20} />;
    }
    if (projectCodeState === "success") {
      return (
        <Box display="flex" alignItems="center" columnGap={0.5}>
          <Check color="success" fontSize="small" />
          <Typography variant="caption" color="textSecondary">
            saved
          </Typography>
        </Box>
      );
    }
    if (projectCodeState === "error") {
      return (
        <Tooltip title="Failed to save project code. Please try again.">
          <Box display="flex" alignItems="center" columnGap={0.5}>
            <ErrorIcon color="error" fontSize="small" />
            <Typography variant="caption" color="error">
              error
            </Typography>
          </Box>
        </Tooltip>
      );
    }
    return <Box />;
  }, [projectCodeState]);

  return (
    <TextField
      autoFocus
      name="ProjectCode"
      defaultValue={projectCode || DEFAULT_PROJECT_CODE}
      required
      label="Project code"
      fullWidth
      validate={required}
      InputProps={{
        endAdornment: projectCodeEndAdornment,
      }}
    />
  );
};
